"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sparkles, Shield, ArrowRight, Play, ChevronRight, Plus, Linkedin, Instagram } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function RubricheConsumer() {
  const {
    posts,
    loading,
    error,
    setWPQuery
  } = usePosts();
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [displayedPosts, setDisplayedPosts] = useState<any[]>([]);
  useEffect(() => {
    if (posts.length > 0) {
      const filtered = activeTag ? posts.filter((post: any) => post.tags?.some((t: any) => t.slug === activeTag)) : posts;
      setDisplayedPosts(filtered.slice(0, 9));
      setCurrentPage(0);
    } else {
      setDisplayedPosts([]);
    }
  }, [posts, activeTag]);
  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    const nextPosts = posts.slice(0, (nextPage + 1) * 9);
    setDisplayedPosts(nextPosts);
    setCurrentPage(nextPage);
  };
  const allTags = [{
    slug: "comunicazione",
    name: "Comunicazione"
  }, {
    slug: "brand",
    name: "Brand"
  }, {
    slug: "marketing",
    name: "Marketing"
  }, {
    slug: "ai",
    name: "AI"
  }, {
    slug: "fotografia",
    name: "Fotografia"
  }, {
    slug: "cinema",
    name: "Cinema"
  }, {
    slug: "mondo-del-lavoro",
    name: "Mondo del lavoro"
  }, {
    slug: "viaggi",
    name: "Viaggi"
  }, {
    slug: "lionia-project",
    name: "Lionia Project"
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  if (loading && displayedPosts.length === 0) {
    return <div className="text-center text-zinc-400 py-20">Caricamento articoli...</div>;
  }
  if (error) {
    return <div className="text-center text-zinc-400">Errore nel caricamento: {error}</div>;
  }
  if (posts.length === 0 && !loading) {
    return <div className="text-center text-zinc-400 py-20">Nessun articolo trovato. Controlla i filtri o torna più tardi.</div>;
  }
  return <>
      <div className="flex flex-wrap justify-center gap-2 mb-20 bg-zinc-900/30 p-2 rounded-lg border border-zinc-800/50 backdrop-blur-sm max-w-fit mx-auto">
        <Button variant="ghost" onClick={() => {
        setActiveTag(null);
        setCurrentPage(0);
        setWPQuery(new WP_Query({
          post_type: "post",
          per_page: 9
        }));
      }} className={cn("px-5 py-2 h-auto rounded-lg text-sm font-medium transition-all duration-300 shadow-none", !activeTag ? "bg-primary text-black hover:bg-primary/90" : "text-zinc-400 hover:text-white hover:bg-white/5")}>
          Tutti
        </Button>
        {allTags.map((tag, i) => <Button key={tag.slug} data-index={i} variant="ghost" onClick={() => {
        const isClearing = activeTag === tag.slug;
        const newTag = isClearing ? null : tag.slug;
        setActiveTag(newTag);
        setCurrentPage(0);
        if (newTag) {
          setWPQuery(new WP_Query({
            post_type: "post",
            per_page: 9,
            tax_query: [{
              taxonomy: 'post_tag',
              terms: [newTag],
              field: 'slug'
            }]
          }));
        } else {
          setWPQuery(new WP_Query({
            post_type: "post",
            per_page: 9
          }));
        }
      }} className={cn("px-5 py-2 h-auto rounded-lg text-sm font-medium transition-all duration-300 shadow-none", activeTag === tag.slug ? "bg-primary text-black hover:bg-primary/90" : "text-zinc-400 hover:text-white hover:bg-white/5")}>
            {tag.name}
          </Button>)}
      </div>

      <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
      once: true,
      amount: 0.1
    }} className="grid grid-cols-1 md:grid-cols-6 gap-6">
        {displayedPosts.map((post, i) => {
        const isFeatured = i === 0;
        const firstTag = post.tags?.find((t: any) => allTags.some(at => at.slug === t.slug)) || post.tags?.[0];
        return <motion.div key={post.id} variants={itemVariants} data-index={i} className={cn("h-full", isFeatured ? "md:col-span-4 md:row-span-2" : "md:col-span-2")}>
              <Card className="h-full overflow-hidden border-zinc-800 bg-[#0c0c0c] hover:border-primary transition-all duration-500 group flex flex-col rounded-lg py-0">
                <div className={cn("relative w-full overflow-hidden bg-black", isFeatured ? "h-80 md:h-[450px]" : "h-56")}>
                  <div className="absolute top-4 left-4 size-6 border-t-2 border-l-2 border-primary/40 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-4 right-4 size-6 border-b-2 border-r-2 border-primary/40 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="absolute top-4 left-4 z-30">
                    {firstTag && <Badge onClick={e => {
                  e.preventDefault();
                  setActiveTag(firstTag.slug);
                  setCurrentPage(0);
                  setWPQuery(new WP_Query({
                    post_type: "post",
                    per_page: 9,
                    tax_query: [{
                      taxonomy: 'post_tag',
                      terms: [firstTag.slug],
                      field: 'slug'
                    }]
                  }));
                }} className="rounded-lg font-mono text-[10px] uppercase tracking-widest font-bold py-1 px-3 bg-primary text-black border-transparent cursor-pointer hover:bg-primary/80 transition-colors">
                        {firstTag.name}
                      </Badge>}
                  </div>

                  {isFeatured && <div className="absolute top-4 right-4 z-30">
                      <Badge className="bg-black/60 text-white border-white/20 backdrop-blur-md rounded-lg font-mono text-[10px] uppercase tracking-widest py-1 px-3 flex items-center gap-1.5">
                        <span className="size-1.5 rounded-lg bg-primary animate-pulse" />
                        NEW
                      </Badge>
                    </div>}
                  
                  {post.featuredImage ? <>
                      <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover opacity-70 grayscale group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700 group-hover:scale-105" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-transparent to-transparent" />
                    </> : <div className="w-full h-full flex items-center justify-center relative bg-gradient-to-br from-primary/40 to-primary/10">
                      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721445/img/noise.png')] opacity-10 mix-blend-overlay pointer-events-none" />
                      <div className="text-white/20 group-hover:text-primary/60 transition-colors duration-500">
                        <Play className="size-6" />
                      </div>
                    </div>}
                </div>

                <CardContent className="p-8 flex flex-col flex-grow">
                  <div className="mb-6 flex items-center justify-between">
                    <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">
                      AUTORE: Marinela Demrozi
                    </span>
                    <div className="flex items-center gap-2">
                      <Link to="https://linkedin.com" newTab className="size-8 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-primary hover:border-primary transition-all">
                        <Linkedin className="size-4" />
                      </Link>
                      <Link to="https://instagram.com/lionia_project" newTab className="size-8 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-primary hover:border-primary transition-all">
                        <Instagram className="size-6" />
                      </Link>
                    </div>
                  </div>

                  <div className="mb-4">
                    <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">
                      {post.date ? new Date(post.date).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  }) : ''}
                    </span>
                  </div>
                  
                  <h3 className={cn("font-bold font-default text-white mb-4 group-hover:text-primary transition-colors duration-300 tracking-tight", isFeatured ? "text-3xl md:text-4xl" : "text-xl")}>
                    {post.title}
                  </h3>
                  
                  <div className="text-zinc-400 mb-8 flex-grow leading-relaxed text-sm line-clamp-2" dangerouslySetInnerHTML={{
                __html: post.excerpt || ""
              }} />
                  
                  <div className="mt-auto flex flex-col gap-6">
                    <Link to={post.link || "#"} className="inline-flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-widest group/link">
                      <span>Leggi l'articolo</span>
                      <ChevronRight className="size-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                    </Link>

                    <div className="flex items-center gap-3 pt-4 border-t border-zinc-800/50">
                      <img src={post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721445/img/placeholder_avatar.png"} alt={post.author?.name || "Author"} className="size-10 rounded-lg object-cover object-top" loading="lazy" />
                      <span className="text-xs text-zinc-400">{post.author?.name || "Marinela Demrozi"} - Founder di Lionia Project</span>
                    </div>

                    <div className="flex flex-col gap-4 pt-4 border-t border-zinc-800/50">
                      <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">CONDIVIDI ARTICOLO SU LINKEDIN</span>
                      <div className="flex items-center gap-3">
                        <Link to={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(post.link || "")}`} newTab className="size-10 rounded-lg bg-primary text-black flex items-center justify-center hover:bg-primary/90 transition-colors">
                          <Linkedin className="size-5" />
                        </Link>
                        <div className="flex items-center gap-2 ml-auto">
                          <Link to="https://instagram.com" newTab className="size-8 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-primary hover:border-primary transition-all">
                            <Instagram className="size-4" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>;
      })}
      </motion.div>

      {displayedPosts.length < posts.length && <div className="mt-12 text-center">
          <Button variant="outline" onClick={handleLoadMore} className="h-14 px-10 rounded-lg border-zinc-800 text-white hover:bg-primary hover:text-black hover:border-primary transition-all duration-500 font-bold uppercase tracking-widest group">
            Carica altri articoli
            <Plus className="ml-2 size-4 transition-transform duration-300 group-hover:rotate-90" />
          </Button>
        </div>}

      <div className="mt-20 text-center">
        <Button variant="outline" size="lg" className="h-14 px-10 rounded-lg border-zinc-800 text-white hover:bg-primary hover:text-black hover:border-primary transition-all duration-500 font-bold uppercase tracking-widest group" onClick={() => {
        setActiveTag(null);
        setCurrentPage(0);
        setWPQuery(new WP_Query({
          post_type: "post",
          per_page: 9
        }));
      }}>
          Scopri tutti gli articoli
          <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Button>
      </div>
    </>;
}
export default function FeaturedRubriche() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="featuredrubriche" className="bg-[#040404] py-32 relative overflow-hidden">
      <div className="absolute top-48 left-0 w-full h-px bg-zinc-800/50 z-0">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 size-1 bg-primary" />
      </div>

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        <div className="text-center mb-16">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-mono text-xs font-medium tracking-[0.2em] text-primary uppercase mb-6 block">
            § 02 // CHRONOS INDEX
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-5xl md:text-7xl font-extrabold font-default text-white mb-6 tracking-tighter uppercase">
            ARGOMENTI/TAG
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Insights, storie e strategie per brand che vogliono farsi notare.
          </motion.p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 9,
        status: ["publish"]
      }}>
          <RubricheConsumer />
        </PostsProvider>
      </div>
    </section>;
}